import { useState, useEffect, useCallback, Fragment } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Badge } from '../components/ui/badge'
import { useToast } from '../components/ui/use-toast'
import { adminApi } from '../lib/api'
import { RefreshCw, Search, ChevronDown, ChevronRight, ExternalLink, ShieldAlert, AlertTriangle, Info } from 'lucide-react'
import { format } from 'date-fns'

interface AuditEvent {
  id: string
  actorId: string | null
  actorType: string
  actorName: string | null
  actorEmail: string | null
  action: string
  category: string
  resourceType: string | null
  resourceId: string | null
  conversationId: string | null
  spaceId: string | null
  executionId: string | null
  correlationId: string | null
  sourceChannel: string | null
  ipAddress: string | null
  outcome: string
  metadata: Record<string, unknown> | null
  createdAt: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

const CATEGORIES = [
  'messaging', 'conversation', 'agent_execution', 'agent_management',
  'scheduling', 'artifact', 'space', 'auth', 'connector', 'admin', 'security', 'system'
]

const ACTOR_TYPES = ['user', 'agent', 'system', 'scheduler']
const OUTCOMES = ['success', 'failure', 'denied', 'error']

// App URL for conversation deep links (admin runs on :5174, main app on :5173)
const APP_BASE_URL = import.meta.env.VITE_APP_URL || 'http://localhost:5173'

interface SecurityFinding {
  category: string
  severity: string
  pattern: string
  matchedText: string
  confidence: number
}

function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'critical':
      return <ShieldAlert className="h-4 w-4 text-red-400" />
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-400" />
    case 'info':
      return <Info className="h-4 w-4 text-blue-400" />
    default:
      return null
  }
}

function getSeverityBadge(severity: string) {
  switch (severity) {
    case 'critical':
      return <Badge className="bg-red-500/15 text-red-400 border-red-500/30">{severity}</Badge>
    case 'warning':
      return <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-500/30">{severity}</Badge>
    case 'info':
      return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30">{severity}</Badge>
    default:
      return <Badge variant="secondary">{severity}</Badge>
  }
}

function formatCategory(category: string) {
  return category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function isSecurityEvent(action: string) {
  return action === 'security.content.flagged' || action === 'security.content.blocked'
}

function getOutcomeBadge(outcome: string) {
  switch (outcome) {
    case 'success':
      return <Badge className="bg-green-500/15 text-green-400 border-green-500/30">{outcome}</Badge>
    case 'failure':
      return <Badge className="bg-red-500/15 text-red-400 border-red-500/30">{outcome}</Badge>
    case 'denied':
      return <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-500/30">{outcome}</Badge>
    case 'error':
      return <Badge className="bg-red-500/15 text-red-400 border-red-500/30">{outcome}</Badge>
    default:
      return <Badge variant="secondary">{outcome}</Badge>
  }
}

function getActorTypeBadge(actorType: string) {
  switch (actorType) {
    case 'user':
      return <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30">{actorType}</Badge>
    case 'agent':
      return <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/30">{actorType}</Badge>
    case 'system':
      return <Badge variant="secondary">{actorType}</Badge>
    case 'scheduler':
      return <Badge className="bg-orange-500/15 text-orange-400 border-orange-500/30">{actorType}</Badge>
    default:
      return <Badge variant="secondary">{actorType}</Badge>
  }
}

export default function AuditLogsPage() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterActorType, setFilterActorType] = useState<string>('all')
  const [filterOutcome, setFilterOutcome] = useState<string>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const params: Record<string, unknown> = {
        page: currentPage,
        limit: 50,
      }

      if (filterCategory !== 'all') params.category = filterCategory
      if (filterActorType !== 'all') params.actorType = filterActorType
      if (filterOutcome !== 'all') params.outcome = filterOutcome
      if (startDate) params.startDate = new Date(startDate).toISOString()
      if (endDate) params.endDate = new Date(endDate + 'T23:59:59').toISOString()
      if (searchTerm.trim()) params.search = searchTerm.trim()

      const result = await adminApi.getAuditLogs(params as any)
      setEvents(result.data)
      setPagination(result.pagination)
    } catch (error) {
      console.error('Failed to load audit logs:', error)
      toast({
        title: 'Error',
        description: 'Failed to load audit logs',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }, [currentPage, filterCategory, filterActorType, filterOutcome, startDate, endDate, searchTerm, toast])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filterCategory, filterActorType, filterOutcome, startDate, endDate])

  const handleSearch = () => {
    setCurrentPage(1)
    loadData()
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const formatActor = (event: AuditEvent) => {
    if (event.actorName) return event.actorName
    if (event.actorEmail) return event.actorEmail
    if (event.actorId) return event.actorId.slice(0, 12) + '...'
    return '-'
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Audit Logs</h1>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter audit events by category, actor, outcome, and date range</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by actor, resource ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterActorType} onValueChange={setFilterActorType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Actor Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actors</SelectItem>
                {ACTOR_TYPES.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterOutcome} onValueChange={setFilterOutcome}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outcomes</SelectItem>
                {OUTCOMES.map(o => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">From:</span>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">To:</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[160px]"
              />
            </div>
            {(startDate || endDate) && (
              <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate('') }}>
                Clear dates
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Events</CardTitle>
          <CardDescription>
            Showing {events.length} of {pagination.total} total events
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No audit events found</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30px]"></TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Channel</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => (
                    <Fragment key={event.id}>
                      <TableRow
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleRow(event.id)}
                      >
                        <TableCell className="p-2">
                          {event.metadata && Object.keys(event.metadata).length > 0 ? (
                            expandedRows.has(event.id) ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )
                          ) : null}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(new Date(event.createdAt), 'MMM dd, HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getActorTypeBadge(event.actorType)}
                            <span className="text-sm truncate max-w-[150px]" title={event.actorId || undefined}>
                              {formatActor(event)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{event.action}</code>
                        </TableCell>
                        <TableCell>
                          {event.resourceType ? (
                            <div className="text-sm">
                              <span className="text-muted-foreground">{event.resourceType}</span>
                              {event.resourceId && (
                                <span className="ml-1 font-mono text-xs" title={event.resourceId}>
                                  {event.resourceId.length > 12
                                    ? event.resourceId.slice(0, 12) + '...'
                                    : event.resourceId}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getOutcomeBadge(event.outcome)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {event.sourceChannel || '-'}
                        </TableCell>
                      </TableRow>
                      {expandedRows.has(event.id) && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/30 p-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              {event.actorId && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">Actor ID</span>
                                  <span className="font-mono text-xs break-all">{event.actorId}</span>
                                </div>
                              )}
                              {event.actorEmail && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">Actor Email</span>
                                  <span className="text-xs">{event.actorEmail}</span>
                                </div>
                              )}
                              {event.conversationId && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">Conversation</span>
                                  <a
                                    href={`${APP_BASE_URL}/conversations/${event.conversationId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono text-xs break-all text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {event.conversationId}
                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                  </a>
                                </div>
                              )}
                              {event.spaceId && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">Space</span>
                                  <span className="font-mono text-xs break-all">{event.spaceId}</span>
                                </div>
                              )}
                              {event.executionId && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">Execution</span>
                                  <span className="font-mono text-xs break-all">{event.executionId}</span>
                                </div>
                              )}
                              {event.correlationId && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">Correlation</span>
                                  <span className="font-mono text-xs break-all">{event.correlationId}</span>
                                </div>
                              )}
                              {event.ipAddress && (
                                <div>
                                  <span className="text-muted-foreground block text-xs">IP Address</span>
                                  <span className="font-mono text-xs">{event.ipAddress}</span>
                                </div>
                              )}
                              <div>
                                <span className="text-muted-foreground block text-xs">Category</span>
                                <span className="text-xs">{event.category}</span>
                              </div>
                            </div>

                            {/* Security findings detail for content flagged/blocked events */}
                            {isSecurityEvent(event.action) && event.metadata && (
                              <div className="mb-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-xs font-medium">Content Security Findings</span>
                                  {event.metadata.severity ? getSeverityBadge(String(event.metadata.severity)) : null}
                                  {event.metadata.recommendation ? (
                                    <Badge variant="outline" className="text-xs">
                                      {String(event.metadata.recommendation).replace(/_/g, ' ')}
                                    </Badge>
                                  ) : null}
                                </div>

                                {/* Summary row */}
                                <div className="flex flex-wrap gap-4 text-xs mb-2 px-2">
                                  {event.metadata.source ? (
                                    <span><span className="text-muted-foreground">Source:</span> {String(event.metadata.source)}</span>
                                  ) : null}
                                  {event.metadata.trustLevel ? (
                                    <span><span className="text-muted-foreground">Trust:</span> {String(event.metadata.trustLevel)}</span>
                                  ) : null}
                                  {event.metadata.scanMethods ? (
                                    <span><span className="text-muted-foreground">Scan methods:</span> {(event.metadata.scanMethods as string[]).join(', ')}</span>
                                  ) : null}
                                </div>

                                {/* Individual findings */}
                                {Array.isArray(event.metadata.findings) && (event.metadata.findings as SecurityFinding[]).length > 0 ? (
                                  <div className="space-y-2">
                                    {(event.metadata.findings as SecurityFinding[]).map((finding, idx) => (
                                      <div
                                        key={idx}
                                        className="bg-background border rounded p-3 space-y-1.5"
                                      >
                                        <div className="flex items-center gap-2">
                                          {getSeverityIcon(finding.severity)}
                                          <span className="text-xs font-medium">{formatCategory(finding.category)}</span>
                                          {getSeverityBadge(finding.severity)}
                                          <span className="text-xs text-muted-foreground ml-auto">
                                            {Math.round(finding.confidence * 100)}% confidence
                                          </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          <span className="font-medium">Pattern:</span> {finding.pattern}
                                        </div>
                                        <div className="text-xs">
                                          <span className="text-muted-foreground font-medium">Matched:</span>{' '}
                                          <code className="bg-red-500/10 text-red-300 px-1.5 py-0.5 rounded break-all">
                                            {finding.matchedText}
                                          </code>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-muted-foreground px-2">
                                    {event.metadata.findingCount
                                      ? `${event.metadata.findingCount} finding(s) detected (details not available for older events)`
                                      : 'No detailed findings available'}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Raw metadata (collapsed for security events since we render findings above) */}
                            {event.metadata && Object.keys(event.metadata).length > 0 && (
                              <div>
                                <span className="text-muted-foreground block text-xs mb-1">
                                  {isSecurityEvent(event.action) ? 'Raw Metadata' : 'Metadata'}
                                </span>
                                <pre className="bg-background rounded p-3 text-xs overflow-x-auto max-h-[300px]">
                                  {JSON.stringify(event.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-3 text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                    disabled={currentPage === pagination.totalPages}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
